// layerLoadingUtils.ts - Pure functions for layer management

// Types
import {
  shapeToVectorLayer,
  DEFAULT_SHAPE_TRANSPARENCY,
} from '@ais-ui/react-openlayers';
import { isShapeLabelShown } from '../../../Shapes/isShapeShownOnStartUp';
import { getFilters, Settings, VectorTile } from '@ais-ui/websockets';
import { isLayerFavorite } from './layerFavoriteCore';
import { isFilterApplied } from '../../../Filters/utils';
import BaseLayer from 'ol/layer/Base';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { getVectorTilesFromSettings } from '../../../VectorTiles/useVectorTileSettings';

export interface LayerConfig {
  type: string;
  id: string;
  enabled: boolean;
}

export interface LayerLoadingDependencies {
  settings: any;
  enabledWmsLayers: any[];
  sourceIdentifiers: any[];
  doesLayerExist: (id: string) => boolean;
  shapeFetch: (id: string) => Promise<any>;
  addLayer: (...args: any[]) => any;
  addVectorTile: (vectorTile: any, hover: boolean, id?: string) => any;
  addSourceIdentifiersToMap: (...args: any[]) => void;
  addExistingLayerItem: (...args: any[]) => void;
  onWmsToTileLayerWithLayerItem: (...args: any[]) => Promise<any>;
  removeShapeFromFavorites: (id: string) => void;
  toast: (...args: any[]) => void;
  olSession: any;
  filterContext: any;
  openLayersContext: {
    displayProjection: any;
    dataProjection: any;
  };
  toggleLayerVisibility: (layer: BaseLayer, isVisible: boolean) => void;
}

// Create layer ordering from current favorites
export const createLayerOrderingFromFavorites = (
  settings: Settings,
  enabledWmsLayers: any[],
): LayerConfig[] => {
  const layerOrdering: LayerConfig[] = [];

  // Always include ships first (at the bottom)
  layerOrdering.push({ type: 'vessels', id: 'ships-layer', enabled: true });

  // Add favorite shapes (below ships by default)
  const favoriteShapes =
    settings['ribbonPlugin.settings']?.favorite_shapes?.shapes || [];
  favoriteShapes.forEach((shapeId: string) => {
    layerOrdering.push({ type: 'shape', id: shapeId, enabled: true });
  });

  // Always load source identifiers layer, but control visibility based on startup setting
  // This ensures we always have access to source identifier coordinates for coverage zoom functionality
  layerOrdering.push({
    type: 'source-identifiers',
    id: 'source-identifiers',
    enabled: true,
  });

  // Add enabled WMS layers
  enabledWmsLayers.forEach((wmsLayer) => {
    layerOrdering.push({
      type: 'wms',
      id: wmsLayer.wmsLayerInfo.id,
      enabled: true,
    });
  });

  // Add favorite vector tiles
  const vectorTiles = getVectorTilesFromSettings(settings);
  vectorTiles.forEach((vt: VectorTile) => {
    if (vt.enabled) {
      layerOrdering.push({ type: 'vectortile', id: vt.id, enabled: true });
    }
  });

  // Add OpenSeaMap if favorited
  const openSeaMapOrdering =
    settings['ribbonPlugin.settings']?.layer_ordering?.layers || [];
  const hasOpenSeaMap = openSeaMapOrdering.some(
    (l: any) => l.type === 'openseamap' && l.enabled,
  );
  if (hasOpenSeaMap) {
    layerOrdering.push({
      type: 'openseamap',
      id: 'openseamap-seamark',
      enabled: true,
    });
  }

  return layerOrdering;
};

// Determine position based on layer order relative to ships
export const determineLayerPosition = (
  layerIndex: number,
  layerOrdering: LayerConfig[],
): 'above-ships' | 'below-ships' => {
  const shipsIndex = layerOrdering.findIndex((l) => l.type === 'vessels');
  return shipsIndex !== -1 && layerIndex < shipsIndex
    ? 'below-ships'
    : 'above-ships';
};

// Load a single layer by type
export const loadSingleLayerByType = async (
  layerConfig: LayerConfig,
  position: 'above-ships' | 'below-ships' | 'top',
  deps: LayerLoadingDependencies,
): Promise<void> => {
  if (deps.doesLayerExist(layerConfig.id)) {
    return;
  }

  let loadedLayer: BaseLayer | undefined;

  try {
    switch (layerConfig.type) {
      case 'shape':
        loadedLayer = await loadShapeLayer(layerConfig.id, position, deps);
        break;
      case 'coverage':
        await loadCoverageLayer(layerConfig.id, position, deps);
        break;
      case 'wms':
        loadedLayer = await loadWmsLayer(layerConfig.id, position, deps);
        break;
      case 'vectortile':
        loadedLayer = await loadVectorTileLayer(layerConfig.id, position, deps);
        break;
      case 'openseamap':
        loadedLayer = await loadOpenSeaMapLayer(layerConfig.id, position, deps);
        break;
      case 'vessels':
      case 'cambridge-radar':
        // These layers are handled by React components
        break;
      default:
        console.warn(`Unknown layer type: ${layerConfig.type}`);
    }

    // Restore hidden state for favorite layers that were saved as not visible
    if (loadedLayer && !layerConfig.enabled) {
      deps.toggleLayerVisibility(loadedLayer, false);
    }
  } catch {
    deps.toast({
      className: 'load-layer-error-toast',
      title: 'Error!',
      description: `Failed to load layer ${layerConfig.id}`,
      variant: 'destructive',
    });
  }
};

// Individual layer type loaders
const loadShapeLayer = async (
  shapeId: string,
  position: 'above-ships' | 'below-ships' | 'top',
  deps: LayerLoadingDependencies,
): Promise<BaseLayer | undefined> => {
  try {
    const shape = await deps.shapeFetch(shapeId);
    const { displayProjection, dataProjection } = deps.openLayersContext;

    const layer = shapeToVectorLayer(shape, displayProjection, dataProjection);
    const name = shape?.properties?.name || 'No name';

    // Add type: 'shape' to properties for context menu detection
    const propertiesWithType = {
      ...(shape?.properties ?? {}),
      type: 'shape',
      transparency:
        shape?.properties?.transparency ?? DEFAULT_SHAPE_TRANSPARENCY,
    };

    deps.addLayer(
      layer,
      name,
      true, // isRemovableFromLayerControl
      true, // canZoomToFromLayerControl
      true, // canShowLabel
      propertiesWithType,
      shape?.properties?.id,
      isShapeLabelShown(deps.settings, shapeId),
      false, // clickable - no left-click behavior for shapes
      false, // hoverEnabled
      position,
      true, // contextMenuEnabled - enable right-click context menu
    );

    return layer;
  } catch {
    deps.removeShapeFromFavorites(shapeId);
    deps.toast({
      className: 'enable-shape-layer-error-toast',
      title: 'Error!',
      description: `Failed to enable layer ${shapeId}`,
      variant: 'destructive',
    });
    return undefined;
  }
};

const loadCoverageLayer = async (
  layerId: string,
  position: 'above-ships' | 'below-ships' | 'top',
  deps: LayerLoadingDependencies,
): Promise<void> => {
  if (layerId !== 'source-identifiers') {
    return;
  }

  // Check if coverage should be shown based on settings
  if (!isLayerFavorite(deps.settings, 'source-identifiers')) {
    return;
  }

  // No source identifiers available - just skip
  if (!deps.sourceIdentifiers || deps.sourceIdentifiers.length === 0) {
    return;
  }

  // Add the source identifiers to the map using the same exact method as manual addition
  // This ensures click handling works properly
  deps.addSourceIdentifiersToMap(
    deps.addLayer,
    deps.sourceIdentifiers,
    deps.openLayersContext.displayProjection,
    deps.openLayersContext.dataProjection,
  );

  // Note: We call addSourceIdentifiersToMap the same way as manual addition
  // The position parameter is handled internally by the addLayer function
};

const loadWmsLayer = async (
  layerId: string,
  position: 'above-ships' | 'below-ships' | 'top',
  deps: LayerLoadingDependencies,
): Promise<BaseLayer | undefined> => {
  const enabledLayer = deps.enabledWmsLayers.find(
    (wms) => wms.wmsLayerInfo.id === layerId,
  );

  if (!enabledLayer) return undefined;

  try {
    const { tileLayer, layerItem } = await deps.onWmsToTileLayerWithLayerItem(
      enabledLayer.wmsInfo,
      enabledLayer.wmsLayerInfo,
    );

    if (!tileLayer || !layerItem) {
      deps.toast({
        className: 'enable-wms-layer-error-toast',
        title: 'Error!',
        description: `WMS layer ${enabledLayer.wmsLayerInfo.title} is invalid`,
        variant: 'destructive',
      });
      return undefined;
    }

    deps.addExistingLayerItem(tileLayer, layerItem, position);
    return tileLayer;
  } catch {
    deps.toast({
      className: 'enable-wms-layer-error-toast',
      title: 'Error!',
      description: `Failed to enable WMS layer ${layerId}`,
      variant: 'destructive',
    });
    return undefined;
  }
};

const loadVectorTileLayer = async (
  tileId: string,
  position: 'above-ships' | 'below-ships' | 'top',
  deps: LayerLoadingDependencies,
): Promise<BaseLayer | undefined> => {
  const vectorTiles = getVectorTilesFromSettings(deps.settings);
  const vt = vectorTiles.find((v: VectorTile) => v.id === tileId);
  if (!vt) return undefined;

  const layerItem = deps.addVectorTile(vt, true, vt.id);
  return layerItem?.layer;
};

const OPENSEAMAP_LAYER_ID = 'openseamap-seamark';

const loadOpenSeaMapLayer = async (
  layerId: string,
  position: 'above-ships' | 'below-ships' | 'top',
  deps: LayerLoadingDependencies,
): Promise<BaseLayer | undefined> => {
  if (layerId !== OPENSEAMAP_LAYER_ID) return undefined;

  const tileLayer = new TileLayer({
    source: new XYZ({
      url: 'https://t1.openseamap.org/seamark/{z}/{x}/{y}.png',
      crossOrigin: 'anonymous',
      attributions: '© OpenSeaMap contributors',
    }),
    className: 'openseamap-layer',
  });

  deps.addLayer(
    tileLayer,
    'OpenSeaMap',
    true, // isRemovableFromLayerControl
    false, // canZoomToFromLayerControl
    false, // canShowLabel
    { type: 'openseamap' },
    OPENSEAMAP_LAYER_ID,
    false, // showLabel
    false, // clickable
    false, // hoverEnabled
    position,
  );

  return tileLayer;
};

// Load favorite filters
export const loadFavoriteFilters = async (
  favoriteFilters: string[],
  olSession: any,
  filterContext: any,
  toast: any,
): Promise<void> => {
  if (!favoriteFilters || favoriteFilters.length === 0) return;

  try {
    const allFilters = await getFilters(olSession);

    if (!allFilters) {
      toast({
        className: 'fetch-filters-error-toast',
        title: 'Error!',
        description: 'Failed to fetch filters',
        variant: 'destructive',
      });
      return;
    }

    const favoriteFilterObjects = allFilters.filter((filter: any) =>
      favoriteFilters.includes(filter.uuid),
    );

    const filtersToAdd = favoriteFilterObjects.filter(
      (filter: any) => !isFilterApplied(filter, filterContext.customFilters),
    );

    if (filtersToAdd.length > 0) {
      filterContext.setCustomFilters([
        ...filterContext.customFilters,
        ...filtersToAdd,
      ]);
    }
  } catch {
    toast({
      className: 'apply-favorite-filters-error-toast',
      title: 'Error!',
      description: 'Failed to apply favorite filters',
      variant: 'destructive',
    });
  }
};
